import { sendTestWorkerCommand } from './testWorkerClient';

// Thin, typed layer over the owner admin-configuration commands. Every call
// returns the worker's `data` payload or throws a TestWorkerCommandError whose
// `.code` is the domain code (SCHEMA_INVALID, REFERENCE_MISSING, ALREADY_EXISTS,
// SCHEDULE_ALREADY_MATERIALIZED, ...). Reads happen straight from Firestore; only
// writes go through here.

const randomSuffix = () => Math.random().toString(36).slice(2, 10);

// djb2 over stable JSON — same edit ⇒ same key (retry is deduped), a different
// edit to the same entity ⇒ different key (no false IDEMPOTENCY_CONFLICT/409).
const payloadHash = (value) => {
  const json = JSON.stringify(value ?? null);
  let h = 5381;
  for (let i = 0; i < json.length; i += 1) h = ((h << 5) + h + json.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

// The idempotency key travels in an HTTP header, which cannot carry non-ASCII
// bytes — an id like `sch-якобы-табак-…` would make `fetch` throw before the
// request leaves the browser (surfacing as a bogus "no connection" error). Keep
// only header-safe characters and disambiguate with a hash when anything was
// stripped, so the key stays deterministic per (uid, action, id, payload).
const asciiKeyPart = (value) => {
  const str = String(value ?? '');
  const safe = str.replace(/[^A-Za-z0-9._-]/g, '');
  return safe === str ? str : `${safe || 'x'}.${payloadHash(str)}`;
};

// Every admin mutation gets a unique key: a fresh "Save" must always apply,
// even if the previous save set the same values (a deterministic key made the
// repeat a silent no-op — e.g. promote → demote → promote again). Concurrent
// retries of the *same* in-flight request still reuse this key (same request
// object), so double-submits are deduped by the receipt. The payload hash is
// kept only for readability in logs.
export const editKey = (uid, action, id, payload) => `admin:${uid}:${action}:${asciiKeyPart(id)}:${payloadHash(payload)}:${Date.now().toString(36)}${randomSuffix().slice(0, 4)}`;
export const createKey = (uid, action) => `admin:${uid}:${action}:${Date.now()}:${randomSuffix()}`;

const call = (user, path, body, idempotencyKey) =>
  sendTestWorkerCommand({ user, path, body, idempotencyKey });

export function createAdminClient(user) {
  const uid = user?.uid || 'anon';
  const create = (path, action) => (id, data) => call(user, path, { id, data }, createKey(uid, action));
  const update = (path, action) => (id, patch) => call(user, path, { id, patch }, editKey(uid, action, id, patch));

  return Object.freeze({
    // onboarding
    createInvite: (fullName) => call(user, '/commands/admin-create-invite', { fullName: fullName || null }, createKey(uid, 'create-invite')),
    // locations
    createLocation: create('/commands/admin-create-location', 'create-location'),
    updateLocation: update('/commands/admin-update-location', 'update-location'),
    // shift types
    createShiftType: create('/commands/admin-create-shift-type', 'create-shift-type'),
    updateShiftType: update('/commands/admin-update-shift-type', 'update-shift-type'),
    // users
    createUser: create('/commands/admin-create-user', 'create-user'),
    updateUser: update('/commands/admin-update-user', 'update-user'),
    setUserActive: (id, active) => call(user, '/commands/admin-update-user', { id, patch: { active } }, editKey(uid, 'user-active', id, { active })),
    // checklist templates
    createChecklist: create('/commands/admin-create-checklist', 'create-checklist'),
    updateChecklist: update('/commands/admin-update-checklist', 'update-checklist'),
    // task templates
    addTask: (checklistTemplateId, id, data) => call(user, '/commands/admin-add-task', { checklistTemplateId, id, data }, createKey(uid, 'add-task')),
    updateTask: update('/commands/admin-update-task', 'update-task'),
    removeTask: (id) => call(user, '/commands/admin-remove-task', { id }, editKey(uid, 'remove-task', id, null)),
    // schedule entries
    createScheduleEntry: create('/commands/admin-create-schedule-entry', 'create-schedule'),
    updateScheduleEntry: update('/commands/admin-update-schedule-entry', 'update-schedule'),
    cancelScheduleEntry: (id, reason) => call(user, '/commands/admin-cancel-schedule-entry', { id, reason: reason || null }, editKey(uid, 'cancel-schedule', id, null)),
  });
}
